import { message } from "antd";
import { useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../../redux/slices/auth.slice";
import { serverUrl } from "../../nestserver";

/**
 * Switches the acting branch — the exact call the header's branch switcher
 * makes — then lands on that branch's admin home by default. This is what
 * makes a read-only group screen acceptable: managing anything is one click
 * away, in the screen that already does it properly. The default target is
 * the admin home rather than a specific sub-screen because a Montessori
 * branch's route for that may differ from a senior branch's — but a caller
 * that already knows a path exists in both (e.g. Course Board, which both
 * AdminRoutes.js and AdminMontessoriRoutes.js mount at the same
 * `admin/menu/course-board`) can pass it as `destination`.
 */
export default function useGroupBranchSwitch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  const goToBranch = async (schoolId, destination = "/dashboard/admin/home") => {
    if (switching) return;
    setSwitching(true);
    const hide = message.loading("Switching to that branch...", 0);
    try {
      const res = await axios.post(
        `${serverUrl}/auth/select-school`,
        { schoolId },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      hide();
      const { access_token, user: newUser } = res.data?.result || {};
      if (access_token) {
        window.localStorage.setItem("jwt-token", access_token);
        dispatch(loginUser(newUser));
        // Not "/dashboard": useIsGroupConsole treats a group admin as being in
        // group mode on any path other than /dashboard/admin/..., so landing
        // on plain "/dashboard" left every one of these branch-name links
        // dropping you right back into the group console instead of the
        // branch you just picked.
        navigate(destination);
        // Full reload rather than a cache invalidation — see Header.js's
        // selectSchool for why: almost none of the app's react-query keys are
        // namespaced by school.
        window.location.reload();
      }
    } catch (err) {
      hide();
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Could not switch branch",
      );
      setSwitching(false);
    }
  };

  return { goToBranch, switching };
}
